import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Bot,
  Brain,
  Check,
  ChevronDown,
  ClipboardPaste,
  Copy,
  Loader2,
  Volume2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { UBO_PRIMARY, type ChatMessage, type ChatMode } from './aiChatShared';
import { linkifyPageRefs } from './aiChatEditorUtils';
import {
  CreatedInvestmentCard,
  CreatedPageCard,
  DocEditStatusCard,
  StreamingContentPreview,
  SuggestionsCard,
  TypingIndicator,
} from './AIChatMessageParts';

const hasContent = (msg: ChatMessage) =>
  msg.content || msg.toolCalls?.length || msg.thinking;

interface AIChatMessageBubbleProps {
  msg: ChatMessage;
  isStreaming: boolean;
  /** Of dit bericht het laatste in de lijst is (voor streaming-indicatoren). */
  isLast: boolean;
  mode: ChatMode;
  mdComponents: Record<string, React.ComponentType<any>>;
  copiedId: string | null;
  speakingId: string | null;
  ttsSupported: boolean;
  streamingPreview: string;
  currentEditPage: number;
  toolResults: Record<string, { blockId: string } | null>;
  onStop: () => void;
  onCopy: (content: string, msgId: string) => void;
  onApply: (msg: ChatMessage) => void;
  onSpeakToggle: (msg: ChatMessage) => void;
  onNavigateToPage: (page: number) => void;
  onNavigateToBlock: (blockId: string) => void;
  onSwitchToAgent: () => void;
}

export function AIChatMessageBubble({
  msg,
  isStreaming,
  isLast,
  mode,
  mdComponents,
  copiedId,
  speakingId,
  ttsSupported,
  streamingPreview,
  currentEditPage,
  toolResults,
  onStop,
  onCopy,
  onApply,
  onSpeakToggle,
  onNavigateToPage,
  onNavigateToBlock,
  onSwitchToAgent,
}: AIChatMessageBubbleProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1',
        msg.role === 'user' ? 'items-end' : 'items-start',
      )}
    >
      <div
        className={cn(
          'max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm',
          msg.role === 'user'
            ? 'text-white'
            : 'border-l-[3px] bg-white/90 text-slate-800',
        )}
        style={
          msg.role === 'user'
            ? { backgroundColor: UBO_PRIMARY }
            : { borderLeftColor: UBO_PRIMARY }
        }
      >
        {msg.role === 'user' && (
          <div className="whitespace-pre-wrap break-words">{msg.content}</div>
        )}

        {msg.role === 'assistant' && (
          <>
            {msg.thinking && (
              <details className="group mb-2">
                <summary className="flex cursor-pointer select-none items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-slate-600">
                  <Brain className="h-3 w-3" />
                  <span>Denkproces</span>
                  <ChevronDown className="h-2.5 w-2.5 transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-1 max-h-32 overflow-y-auto rounded-md bg-slate-50/80 px-2.5 py-1.5 text-[10px] leading-relaxed text-slate-400">
                  {msg.thinking}
                </div>
              </details>
            )}

            {msg.content ? (
              <div className="break-words text-sm leading-relaxed [&>*:first-child]:mt-0">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}
                >
                  {linkifyPageRefs(msg.content)}
                </ReactMarkdown>
                {mode === 'ask' && msg.content.includes('Agent modus') && !isStreaming && (
                  <button
                    type="button"
                    onClick={onSwitchToAgent}
                    className="mt-2 flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:opacity-90"
                    style={{ backgroundColor: UBO_PRIMARY, borderColor: UBO_PRIMARY }}
                  >
                    <Bot className="h-3.5 w-3.5" />
                    Schakel over naar Agent modus
                  </button>
                )}
              </div>
            ) : (
              !hasContent(msg) &&
              !isStreaming && (
                <span className="text-slate-400">...</span>
              )
            )}

            {msg.toolCalls?.map((tc, idx) => {
              if (tc.name === 'suggest_improvements') {
                return (
                  <SuggestionsCard
                    key={idx}
                    toolCall={tc}
                    onNavigate={onNavigateToPage}
                  />
                );
              }
              if (tc.name === 'create_page') {
                const resultKey = tc._resultKey;
                return (
                  <CreatedPageCard
                    key={idx}
                    toolCall={tc}
                    result={resultKey ? toolResults[resultKey] : undefined}
                    onNavigate={onNavigateToBlock}
                  />
                );
              }
              if (tc.name === 'create_investment_table') {
                const resultKey = tc._resultKey;
                return (
                  <CreatedInvestmentCard
                    key={idx}
                    toolCall={tc}
                    result={resultKey ? toolResults[resultKey] : undefined}
                    onNavigate={onNavigateToBlock}
                  />
                );
              }
              return null;
            })}

            {msg.docEdits?.map((edit, idx) => (
              <DocEditStatusCard
                key={`de-${idx}`}
                edit={edit}
                onNavigate={onNavigateToPage}
              />
            ))}

            {isStreaming && isLast && !msg.content && !msg.thinking && !msg.docEdits?.length && (
              <TypingIndicator action={msg.action} onStop={onStop} />
            )}

            {isStreaming && isLast && msg.thinking && !msg.content && (
              <div className="flex items-center gap-2.5 py-0.5">
                <Brain className="h-4 w-4 animate-pulse" style={{ color: UBO_PRIMARY }} />
                <span className="text-xs font-medium text-slate-500">Nadenken...</span>
                <button
                  type="button"
                  onClick={onStop}
                  className="ml-auto text-[10px] text-slate-400 underline hover:text-slate-600"
                >
                  Stop
                </button>
              </div>
            )}

            {isStreaming && isLast && (msg.docEdits?.some((de) => de.status === 'in_progress') || false) && (
              <>
                <div className="mt-1.5 flex items-center gap-2 py-0.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: UBO_PRIMARY }} />
                  <span className="text-xs font-medium text-slate-500">Document wordt bewerkt...</span>
                  <button
                    type="button"
                    onClick={onStop}
                    className="ml-auto text-[10px] text-slate-400 underline hover:text-slate-600"
                  >
                    Stop
                  </button>
                </div>
                {streamingPreview && currentEditPage > 0 && (
                  <StreamingContentPreview
                    content={streamingPreview}
                    page={currentEditPage}
                  />
                )}
              </>
            )}

            {msg.content &&
              isStreaming &&
              isLast && (
                <div className="mt-1 flex items-center gap-2">
                  <span
                    className="inline-block h-4 w-0.5 animate-pulse rounded-full"
                    style={{ backgroundColor: UBO_PRIMARY }}
                  />
                  <button
                    type="button"
                    onClick={onStop}
                    className="text-[10px] text-slate-400 underline hover:text-slate-600"
                  >
                    Stop
                  </button>
                </div>
              )}
          </>
        )}

        {msg.role === 'assistant' &&
          (msg.content || msg.toolCalls?.length) &&
          !isStreaming &&
          !msg.content.startsWith('Fout:') && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1 border-t border-slate-200/80 pt-2">
              {msg.applied && (
                <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                  <Check className="h-3 w-3" />
                  Toegepast
                </span>
              )}
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onCopy(msg.content, msg.id)}
                title="Kopieer"
                className="h-7 w-7 text-slate-400 hover:text-slate-700"
              >
                {copiedId === msg.id ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
              {ttsSupported && msg.content && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  type="button"
                  onClick={() => onSpeakToggle(msg)}
                  title={speakingId === msg.id ? 'Stop voorlezen' : 'Voorlezen'}
                  className={cn(
                    'h-7 w-7',
                    speakingId === msg.id
                      ? 'text-violet-600'
                      : 'text-slate-400 hover:text-slate-700',
                  )}
                >
                  <Volume2
                    className={cn(
                      'h-3.5 w-3.5',
                      speakingId === msg.id && 'opacity-90',
                    )}
                  />
                </Button>
              )}
              {!msg.applied && msg.content && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onApply(msg)}
                  title="Toepassen in editor"
                  className="h-7 w-7 text-slate-400 hover:text-[rgb(0,7,236)]"
                >
                  <ClipboardPaste className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
      </div>
    </div>
  );
}
